import { Timestamp } from 'firebase/firestore';
import { DateTime } from 'luxon';
import { mapState } from 'pinia';
import { userTimeZone } from 'src/contexts/identityAndTenancy';
import { toCalendarDate } from 'src/kernel/time';
import { auth } from 'src/store/auth';

/**
 * Implements the same methods as in src/composables/time.js
 *
 * Most of our components are still options api based, this means that we need a "setup()" function to be able to use
 * composables. If we use a setup function both in a mixin and also in a component that uses this mixin, the "setup()"
 * function inside the mixin will be ignored. To avoid even more implicit methods usages and/or issues tied to "setup()"
 * getting ignored, mixins should use this mixin instead of "useTime" composable.
 */

/** @typedef {import('../composables/timeTypes').DateParams} DateParams */
/** @typedef {import('../composables/timeTypes').SingularDateParams} SingularDateParams */
/** @typedef {import('../composables/timeTypes').AdjustDateOptions} AdjustDateOptions */
/** @typedef {import('../composables/timeTypes').AddSubtractDateOptions} AddSubtractDateOptions */

export default {
  computed: {
    ...mapState(auth, ['userDetails']),

    /**
     * Difference in minutes between device timezone and user selected timezone
     */
    deviceToTimezoneOffset() {
      const deviceOffset = DateTime.now().offset;
      const timezoneOffset = DateTime.now().setZone(this.userDetails?.timeZonePreference).offset;
      return timezoneOffset - deviceOffset;
    },

    /**
     * IANA timezone string
     */
    currentIANATimezone() {
      return userTimeZone(this.userDetails, Intl.DateTimeFormat().resolvedOptions().timeZone);
    },
  },
  methods: {
    /**
     * Today's calendar date ('YYYY-MM-DD') in the user's timezone. Reads the clock, so call it from
     * the screen deciding what "today" means, never from a rule that should take the date as input.
     *
     * @return {string}
     */
    todayCalendarDate() {
      return toCalendarDate(new Date(), this.currentIANATimezone);
    },

    propToLibraryDate(value = null) {
      let vanillaDate = null;
      if (value instanceof Timestamp) {
        vanillaDate = value.toDate();
      } else {
        vanillaDate = new Date(value);
      }

      return DateTime.fromJSDate(vanillaDate).setZone(this.userDetails?.timeZonePreference);
    },

    /**
     * Returns a date string in a similar format as native .toLocaleDateString() method, can also be provided options to
     * change the format
     *
     * @param {Timestamp|Date|string} date
     * @param {Object=} opts
     * @param {string=} opts.locales
     * @param {Object=} opts.options
     * @return {string}
     */
    getLocaleDateString(date, { locales = undefined, options = null } = {}) {
      let customOptions = options;
      const paramsCheck = ['weekday', 'year', 'month', 'day', 'dateStyle'];
      if (options && paramsCheck.every((param) => !options[param])) {
        customOptions = { year: 'numeric', month: 'numeric', day: 'numeric', ...customOptions };
      }
      return this.propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions || DateTime.DATE_SHORT);
    },

    /**
     * Returns a date string in a similar format as native .toLocaleTimeString() method, can also be provided options to
     * change the format
     *
     * @param {Timestamp|Date|string} date
     * @param {Object=} opts
     * @param {string=} opts.locales
     * @param {Object=} opts.options
     * @return {string}
     */
    getLocaleTimeString(date, { locales = undefined, options = null } = {}) {
      let customOptions = options;
      const paramsCheck = ['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits', 'timeStyle'];
      if (options && paramsCheck.every((param) => !options[param])) {
        customOptions = { hour: 'numeric', minute: 'numeric', second: 'numeric', ...customOptions };
      }
      return this.propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions
        || DateTime.TIME_WITH_SECONDS);
    },

    /**
     * Returns a date string in a similar format as native .toLocaleString() method, can also be provided options to
     * change the format
     *
     * @param {Timestamp|Date|string} date
     * @param {Object=} opts
     * @param {string=} opts.locales
     * @param {Object=} opts.options
     * @return {string}
     */
    getLocaleString(date, { locales = undefined, options = null } = {}) {
      let customOptions = options;
      const paramsCheck = ['weekday', 'year', 'month', 'day', 'dayPeriod', 'hour', 'minute', 'second',
        'fractionalSecondDigits', 'dateStyle', 'timeStyle'];
      if (options && paramsCheck.every((param) => !options[param])) {
        customOptions = {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          ...customOptions,
        };
      }
      return this.propToLibraryDate(date).setLocale(locales).toLocaleString(customOptions
        || DateTime.DATETIME_SHORT_WITH_SECONDS);
    },

    /**
     * Returns a date string in a similar format as native .toString() method
     *
     * @param {Timestamp|Date|string} date
     * @return {string}
     */
    getString(date) {
      return this.propToLibraryDate(date).toFormat("ccc MMM dd yyyy HH:mm:ss ZZZZ '('ZZZZZ')'");
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {string} format
     * @param {string=} locale
     * @return {string}
     */
    formatDate(date, format, locale) {
      return this.propToLibraryDate(date).setLocale(locale).toFormat(format || "yyyy-MM-dd'T'HH:mm:ss.SSSZZ");
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {string} mask
     * @param {string=} locale
     * @param {boolean=} useDeviceTimezone - ignore user selected timezone
     * @return {Date}
     */
    extractDate(date, mask, locale, useDeviceTimezone = false) {
      return DateTime.fromFormat(date, mask, {
        zone: useDeviceTimezone ? 'local' : this.userDetails?.timeZonePreference,
        locale,
      }).toJSDate();
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {AdjustDateOptions} options
     * @return {Date}
     */
    adjustDate(date, options) {
      return this.propToLibraryDate(date).set(options).toJSDate();
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    getDayOfWeek(date) {
      return this.propToLibraryDate(date).weekday;
    },

    /**
     * Returns a day suffix (st, nd, rd, th), only for en locale
     *
     * @param {number} day
     * @return {string}
     */
    getDaySuffix(day) {
      const suffixes = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
      const pluralRules = new Intl.PluralRules('en-US', { type: 'ordinal' });
      return suffixes[pluralRules.select(day)];
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {Object} options
     * @param {boolean} options.zeroBased
     * @return {number}
     */
    getMonth(date, { zeroBased = false } = {}) {
      const { month } = this.propToLibraryDate(date);
      return zeroBased ? month - 1 : month;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    getDate(date) {
      return this.propToLibraryDate(date).day;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {Object} options
     * @param {boolean} options.sundayFirst
     * @param {boolean} options.zeroBased
     * @return {number}
     */
    getDay(date, { sundayFirst = true, zeroBased = true } = {}) {
      let { weekday } = this.propToLibraryDate(date); // monday = 1, sunday = 7
      if (sundayFirst) {
        weekday += 1;
        if (weekday > 7) {
          weekday = 1;
        }
      }
      if (zeroBased) {
        weekday -= 1;
      }
      return weekday;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    getFullYear(date) {
      return this.propToLibraryDate(date).year;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    getHours(date) {
      return this.propToLibraryDate(date).hour;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    getMinutes(date) {
      return this.propToLibraryDate(date).minute;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    getSeconds(date) {
      return this.propToLibraryDate(date).second;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    getMilliseconds(date) {
      return this.propToLibraryDate(date).millisecond;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {number}
     */
    daysInMonth(date) {
      return this.propToLibraryDate(date).daysInMonth;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {SingularDateParams} unit
     * @return {Date}
     */
    endOfDate(date, unit) {
      return this.propToLibraryDate(date).endOf(unit).toJSDate();
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {SingularDateParams} unit
     * @return {Date}
     */
    startOfDate(date, unit) {
      return this.propToLibraryDate(date).startOf(unit).toJSDate();
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {AddSubtractDateOptions} options
     * @return {Date}
     */
    addToDate(date, options) {
      return this.propToLibraryDate(date).plus(options).toJSDate();
    },

    /**
     * @param {Timestamp|Date|string} date
     * @param {AddSubtractDateOptions} options
     * @return {Date}
     */
    subtractFromDate(date, options) {
      return this.propToLibraryDate(date).minus(options).toJSDate();
    },

    /**
     * @param {Timestamp|Date|string} date1
     * @param {Timestamp|Date|string} date2
     * @param {DateParams} unit
     * @return {Date}
     */
    isSameDate(date1, date2, unit) {
      return this.propToLibraryDate(date1).hasSame(this.propToLibraryDate(date2), unit);
    },

    /**
     * @param {Timestamp|Date|string} date1
     * @param {Timestamp|Date|string} date2
     * @param {DateParams} unit
     * @param {Object} opts
     * @param {'floor'|'round'|'ceil'|'default'} accuracy
     * @return {Date}
     */
    getDateDiff(date1, date2, unit, opts = {}, accuracy = 'floor') {
      const dateDiff = this.propToLibraryDate(date1)
        .diff(this.propToLibraryDate(date2), unit, { conversionAccuracy: 'longterm', ...opts })
        .as(unit);

      if (accuracy !== 'default') {
        return Math[accuracy](dateDiff);
      }
      return dateDiff;
    },

    /**
     * @param {Timestamp|Date|string} dateTarget
     * @param {Timestamp|Date|string} dateFrom
     * @param {Timestamp|Date|string} dateTo
     * @param {Object} options
     * @param {boolean} options.onlyDate
     * @param {boolean} options.inclusiveFrom
     * @param {boolean} options.inclusiveTo
     * @return {boolean}
     */
    isBetweenDates(dateTarget, dateFrom, dateTo, {
      onlyDate = false,
      inclusiveFrom = false,
      inclusiveTo = false,
    } = {}) {
      const unitParam = onlyDate ? 'day' : 'millisecond';
      const targetLuxon = this.propToLibraryDate(dateTarget).startOf(unitParam);
      const fromLuxon = this.propToLibraryDate(dateFrom).startOf(unitParam);
      const toLuxon = this.propToLibraryDate(dateTo).startOf(unitParam);

      const from = inclusiveFrom ? targetLuxon >= fromLuxon : targetLuxon > fromLuxon;
      const to = inclusiveTo ? targetLuxon <= toLuxon : targetLuxon < toLuxon;

      return from && to;
    },

    /**
     * @param {Timestamp|Date|string} date
     * @return {boolean}
     */
    isValid(date) {
      // pulled the implementation from quasar
      return typeof date === 'number' ? true : Number.isNaN(Date.parse(date)) === false;
    },

    /**
     * The same wall clock, expressed in another timezone: 1pm in the user's timezone becomes 1pm in `customTimezone`.
     * The returned date is **not the same instant**. Used when internal libraries rely on native Date implementation,
     * but we need to display a date as if it was in a different timezone
     *
     * Should be used only if the date is immediately transformed to a string, as while the displayed date is correct,
     * the timestamp will be wrong (e.g. the time is 1pm +0, and we need to make it +2, this method will return 3pm +0),
     * so this should not be used if there are additional date manipulations later
     *
     * Pair it with {@link getTimezoneOffsetDateToLocal} for any value read back out of that library.
     *
     * @param {Timestamp|Date|string} date
     * @param {string=} customTimezone
     * @return {Date}
     */
    getLocalToTimezoneOffsetDate(date, customTimezone = 'system') {
      return this.propToLibraryDate(date).setZone(customTimezone, { keepLocalTime: true }).toJSDate();
    },

    /**
     * Inverse of {@link getLocalToTimezoneOffsetDate}: reads the `customTimezone` fields of `date` as a wall clock in
     * the user's timezone and returns the instant that denotes. Ambiguous only for a wall clock inside that
     * timezone's own DST gap, which is an hour that never happened there.
     *
     * @param {Date} date
     * @param {string=} customTimezone
     * @return {Date}
     */
    getTimezoneOffsetDateToLocal(date, customTimezone = 'system') {
      return this.propToLibraryDate(date)
        .setZone(customTimezone)
        .setZone(this.userDetails?.timeZonePreference, { keepLocalTime: true })
        .toJSDate();
    },
  },
};
